import {SubjectType} from './redux/actions'

export type Employee = {
  MetaFlags: number
  Name: string
  Email: string
}

export type UserAppSettings = {
  auction_view: 'grid' | 'list'
  budget: number
}

export type User = {
  employee: Employee
  ars_id: number
  photo_url: string
  notifications_subscription: Array<{Target: number}>
  app_settings: UserAppSettings | null
  authority: any
  impersonated_by: number | undefined
  domain: string
  locale: 'de' | 'en' | 'fr'
}

export type EquipmentFlag = {
  field: string
  value: number
  label: string
}

export type Codes = {
  Make: {
    [key: number]: {label: string, value: number}
  }
  Model: {
    [makeKey: number]: Array<{
      label: string
      value: number
      Make: number
    }>
  }
  PostalCodes: Array<{
    PostalCode: string,
    City: string
  }>

  Fuel: {
    [key: number]: {
      label: string, value: number
    }
  }
  Body: {
    [key: number]: {
      label: string, value: number
    }
  }
  EquipmentFlags: Array<EquipmentFlag>
}

export type BiddingStep = {from: number, to: number, step: number}

export type AuctionSettings = {
  agbUrl: string
  agbUrls: string[]
  auctionTypes: {
    auction: boolean
    immediate: boolean
    single: boolean
  }
  bidChangeTime: number
  biddingSteps: Array<BiddingStep>
  hotBid: {starts: number, resets: number}
  immediateAfterFirstRound: boolean
  legalNotice: string
  neededBids: number
  news: string | null
  pickupCode: string | null
  showNotificationOnMarketPriceReached: boolean
  showPriceCalls: boolean
  showFilters: boolean
  showProtocol: boolean
  notifyOutbidInSealed: boolean
  notes: string | null | undefined
  dashboard: {
    showSumHighestBids: boolean
    showSumMaxBids: boolean
    showHighestBidderCount: boolean
    showAuctionedCount: boolean
  } | undefined
}

export type WebSocketSettings = {
  key: string
  host: string
  port: string
  encrypted: string
}

export type AuctionNotificationBody = {
  message: string
  auctionId: number
  make: number
  shortDescription: string
}

export type Notification = {
  created_at: string
  created_by: string
  icon: string | null
  id: number
  read: 0 | 1
  updated_at: string
  user_id: number
  body: AuctionNotificationBody
}

export type Dealer = {
  Id: number
  Firm: string | null
  FirmExtra: string | null
  Street: string | null
  PostalCode: string | null
  City: string | null
}

export type VehicleExtra = {
  DamageDesc: string | null
  DamageDescDetails: string | null
  DamageDescOther: string | null
  KnownLacks: string | null
}

export type AuctionBid = {
  Bid: number
  BidderId: number
  OldBid: number
}

export type BidAgent = {
  max_bid: number
}

export type AuctionRoundType = 0 | 1 | 2

export type AuctionRound = {
  Id: number
  bids: Array<AuctionBid>
  bid: AuctionBid
  agents: Array<BidAgent>
  Type: AuctionRoundType
  UseNetPrices: boolean
  NetPrice: number
  GrossPrice: number
  PriceCall: number | null
  FixedPrice: number | null
  EndsOn: string
  PreAuction: boolean
  MinPrice: number | null
  StartsOn: string | null
  Winner: number | null /** Winner BidId */
  PickupCode: number | null
  winner: AuctionBid
}

export type WatchingAuction = {
  user_id: number
  id: number
}

export type AuctionHistoryEntry = {
  Id: number
  Action: string
  causer: Employee
  InsertedOn: string
}

export type AuctionHistory = Array<AuctionHistoryEntry>

export type AuctionStatus = 'ongoing' | 'hot-bid' | 'ended' | 'not-started-yet' | 'cancelled'

export type AuctionIgnored = {
  id: number
}

export type CiVehicle = {
  DealerId: number
  DossierId: number
  Vin: string
}

export type Auction = {
  round: AuctionRound
  highestBid: number | null
  highestBidder: AuctionBid
  Id: number
  watched: Array<WatchingAuction>
  watched_by_user: WatchingAuction | null
  history: AuctionHistory
  subjects: Array<Subject>
  status: AuctionStatus
  ignored: AuctionIgnored | null
  isOutbid?: boolean | number // Can be boolean from websocket or 0/1 from database
}

export type Subject = {
  AuctionId: number
  SubjectId: number
  SubjectType: SubjectType
  vehicle: Vehicle
  tireset: Tireset
  leading: Leading
  leasing: Leasing
}

export type Indicators = {
  auctioned: number
  highestBidder: number
  sumBids: number
  sumMaxBids: number
}

export type VehicleDocument = {
  Id: number
  Title: string
  Url: string
}

export enum TireType {
  ALLWEATHER,
  WINTER,
  SUMMER
}

export type Tire = {
  Position: 1 | 2 | 3 | 4
  AdditionalFeature: string | null
  Rdks: boolean
  TireDotNumber: string
  TireHeight: number
  TireInch: number
  TireIndex: string
  TireMake: string
  TireProfileDepth: number
  TireType: TireType.ALLWEATHER | TireType.SUMMER | TireType.WINTER
  TireWidth: number
  TiresetId: number
  UpdatedBy: number
  WheelCap: 0 | 1 | 2
  WheelHoleCircle: string
  WheelIsOriginal: number
  WheelMake: string
  WheelOffset: string
  WheelType: 0 | 1
  WheelWidth: string
  WheelColor: string
  WheelCondition: string
  images: Array<string>
}

export type Tireset = {
  Id: number
  Make: number
  Model: number
  dealer: Dealer
  images: Array<string>
  Notes: string
  StockLabel: string
  Code: string
  ProductName?: string
  NFZ: boolean
  tires: Array<Tire>
}

type Document = {
  Id: number
  Url: string
  Title: string
}

export type Leasing = {
  dealer: Dealer
  images: Array<string>
  documents: Array<Document>
  Id: number
  Vin: string
  Make: number
  Model: number
  ShortDescription: string
}

export type Subjects = Leading | Leasing | Vehicle | Tireset

export type Leading = {
  dealer: Dealer
  images: Array<string>
  documents: Array<Document>
  ClientType: string
  CurrentTask: number
  DealerId: number
  Done: boolean
  EmployeeId: number
  Id: number
  InsertedBy: number
  InsertedOn: string
  InvoiceDealerId: number
  Kilometers: number
  LicencePlate: string
  LocalKey: null
  LocationDealerId: number
  Make: number
  MarketingDealerId: number
  Model: number
  OffsettingTransaction: number
  PreviousOwnerName: string
  ReadyDate: null
  RegistrationDate: string
  Sold: null
  TakeInDate: string
  Type: number
  UpdatedBy: number
  UsageType: 1
  Vin: string
  VtiFirstCallDate: string
  VtiFirstCallIsDone: string
  VtiFirstCallIsDoneBy: string
  VtiFirstCallIsDoneOn: string
  VtiIsFirstCall: string
}

export type VehicleEnvkv = {
  Type: 'Combustion' | 'Electric' | 'PluginHybrid' | null
  EmissionsCombined: number | null
  EmissionsCombinedWeighted: number | null
}

export type VehicleConsumption = {
  TestProcedure: 'wltp' | 'nedc',
  Type: 'co2' | 'consumption',
  Unit: string | null,
  Low: number | null,
  Medium: number | null,
  High: number | null,
  ExtraHigh: number | null,
  Combined: number | null,
  HybridElectricCombined: number | null
}

export type Vehicle = {
  Id: number
  images: Array<string>
  dealer: Dealer
  extra: VehicleExtra
  Make: number
  Model: number
  ShortDescription: string
  Power: number
  Kilometers: number
  RegistrationDate: string
  Transmission: number
  Fuel: number
  Condition: string
  documents: Array<VehicleDocument>
  ci_vehicle: CiVehicle | null
  LongDescription: string
  Remarks: string | null
  Vin: string
  Hsn: string
  Tsn: string
  LocalCode: string
  LocalKey: string
  Location: string
  LicensePlate: string
  Category: number
  Type: number
  SellFlags: number
  SellFlags2: number
  Body: number
  Cabin: number
  Year: number
  ModelYear: number
  Label: string
  NumOwners: number
  InspectionDate: string
  DeliveryDate: string
  TowingCapacityBraked: number
  TowingCapacityUnbraked: number
  Engine: number
  Drive: number
  Displacement: number
  NumCylinders: number
  NumGears: number
  TankCapacity: number
  Color: number
  Miscellaneous: string
  Interior: number
  EquipmentLine: number
  SeatCover: number
  NumDoors: number
  NumSeats: number
  EmissionGroup: number
  EmissionsBadge: number
  EfficiencyClass: number
  TireEfficiencyClass: number
  EmissionsCo2: number
  EmissionsCo2Wltp: number
  ConsumIntraUrban: number
  ConsumExtraUrban: number
  ConsumCombined: number
  ConsumHybridElectro: number
  consumption: VehicleConsumption[]
  Weight: number | null
  envkv: VehicleEnvkv | null
}
